import { Component, AfterViewInit, ElementRef, HostListener, OnInit, ViewChild, OnDestroy, Renderer2, Inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs';
import { DOCUMENT,CommonModule } from '@angular/common';

@Component({
  selector: 'app-agi-implementation-guide',
  templateUrl: './agi-implementation-guide.component.html',
  styleUrls: ['./agi-implementation-guide.component.scss']
})
export class AGIImplementationGuideComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(private router: Router, private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }
  private jsonLdScript: any;
  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route) => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route: { data: any; }) => route.data)
    )
  }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }
  injectSchemaMarkup(): void {
    const fullGraphSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/agi-implementation-guide#webpage",
          "url": "https://www.kalpitatechnologies.com/tech-blogs/agi-implementation-guide",
          "name": "Top 10 Things to Know Before Implementing AGI in Your Business (2025 Guide) | Kalpita Technologies",
          "description": "Comprehensive 2025 guide covering the top 10 critical considerations for implementing Artificial General Intelligence (AGI) in business.",
          "isPartOf": {
            "@type": "WebSite",
            "@id": "https://www.kalpitatechnologies.com/#website",
            "name": "Kalpita Technologies",
            "url": "https://www.kalpitatechnologies.com"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/agi-implementation-guide#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/", "name": "Home" } },
            { "@type": "ListItem", "position": 2, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/tech-blogs/blog", "name": "Blog Index" } },
            { "@type": "ListItem", "position": 3, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/tech-blogs/agi-implementation-guide", "name": "AGI Implementation Guide" } }
          ]
        },
        {
          "@type": "BlogPosting",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/agi-implementation-guide#blogposting",
          "headline": "Top 10 Things to Know Before Implementing AGI in Your Business (2025 Guide)",
          "articleBody": "Artificial General Intelligence (AGI) is rapidly evolving from a theoretical concept into a tangible technology poised to reshape business models...",
          "author": { "@type": "Person", "name": "Grace Catharine" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "@id": "https://www.kalpitatechnologies.com/#organization"
          },
          "datePublished": "2025-08-21",
          "image": "https://www.kalpitatechnologies.com/assets/resources/blog/agi-implementation-guide/agi-implementation-banner.webp"
        },
        {
          "@type": "HowTo",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/agi-implementation-guide#howto",
          "name": "How To Implement AGI in Business Workflows",
          "step": [
            { "@type": "HowToStep", "position": 1, "name": "Map Key Processes", "text": "Analyze existing workflows to identify areas best suited for AGI integration." },
            { "@type": "HowToStep", "position": 2, "name": "Select Pilot Projects", "text": "Choose small-scale, manageable pilot projects targeting high-impact challenges." },
            { "@type": "HowToStep", "position": 7, "name": "Scale Upon Validation", "text": "Expand AGI integration progressively after demonstrating value." }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/agi-implementation-guide#faqpage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is AGI and how does it differ from narrow AI?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Artificial General Intelligence (AGI) embodies versatile, human-like intelligence capable of understanding, learning, and applying knowledge across diverse domains."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/resources/kalpita-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(fullGraphSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  nav(url: any) {
    this.router.navigateByUrl(url)
  }

  @ViewChild('tableresponsiveImage', { static: false }) image!: ElementRef<HTMLImageElement>;

  originalWidth = 1920;
  originalHeight = 797;

  ngAfterViewInit(): void {
    this.resizeImageMap();
    this.image.nativeElement.onload = () => this.resizeImageMap();
  }

  @HostListener('window:resize')
  resizeImageMap(): void {
    const img = this.image.nativeElement;
    if (!img) return;

    const wRatio = img.clientWidth / this.originalWidth;
    const hRatio = img.clientHeight / this.originalHeight;

    document.querySelectorAll<HTMLAreaElement>('map[name="table-image-map"] area')
      .forEach(area => {
        const rawCoords =
          area.getAttribute('data-original-coords')
          || area.getAttribute('coords')
          || '';
        if (!rawCoords) return;

        area.setAttribute('data-original-coords', rawCoords);
        const scaled = rawCoords
          .split(',')
          .map((c, i) =>
            Math.round(
              Number(c) * (i % 2 === 0 ? wRatio : hRatio)
            )
          )
          .join(',');

        area.setAttribute('coords', scaled);
      });
  }

  scrollToSection(sectionId: string): void {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const yOffset = 5 * 16;
    const top =
      el.getBoundingClientRect().top + window.pageYOffset - yOffset;
    window.scrollTo({ top, behavior: 'smooth' });
  }
}
